import { Button, Modal, Typography } from "antd";
import MoreOptionsIcon from "@/assets/icons/MoreOptionsIcon.svg?react";
import DeleteIcon from "@/assets/icons/delete.svg?react";
import CadenceIcon from "@/assets/icons/CadenceIcon.svg?react";
import SharedIcon from "@/assets/icons/SharedIcon.svg?react";
import InsightInformationDrawer from "../Insights/InsightInformationDrawer";
import { useAlertDeletion } from "@/hooks/useAlertDeletion";
import AlertStatusTag from "./AlertStatusTag";
import { formattedDate } from "@/utils/dateUtils";
import { getAlertDisplayCondition, getAlertStatus } from "../Insights/utils";
import { capitalizeFirst } from "@/utils/textFormattingUtils";
import MenuDropdown from "../Insights/MenuDropdown";
import { useSuperuserMode } from "@/contexts/useSuperuser";
import type { AlertWithInsightProps } from "../Insights/insights";
import { useState } from "react";
import type { KeyboardEvent } from "react";

const { Text, Paragraph } = Typography;

type ExpandedAlertCardProps = {
  alert: AlertWithInsightProps;
};

export default function ExpandedAlertCard({ alert }: ExpandedAlertCardProps) {
  const { contextHolder, deleteAlertWithFeedback } = useAlertDeletion();
  const { isSuperuserMode } = useSuperuserMode();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  if (!alert?.insight || !alert?.id) return null;

  const canDelete =
    !alert.insight.is_shared || (alert.insight.is_shared && isSuperuserMode);

  const handleDelete = () => {
    deleteAlertWithFeedback(alert.id, () => setOpenDeleteModal(false));
  };

  const handleDeleteClick = () => {
    setOpenDeleteModal(true);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsDrawerOpen(true);
    }
  };

  const menuItems = [
    {
      key: "delete",
      label: (
        <div
          className="flex items-center gap-2 text-[#980000]"
          onClick={handleDeleteClick}
        >
          <DeleteIcon className="w-6 h-6" />
          Delete
        </div>
      ),
    },
  ];

  const status = getAlertStatus(alert);
  const isAlerted = status === "alerted";

  return (
    <>
      {contextHolder}
      <div
        className="rounded-lg hover:shadow-md transition-shadow duration-200 cursor-pointer bg-white"
        onClick={() => setIsDrawerOpen(true)}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Click to view alert details"
        style={isAlerted ? { borderWidth: "1px", borderColor: "#EBD1E0" } : {}}
      >
        <div
          className="px-4 py-3 rounded-t-lg"
          style={
            isAlerted
              ? {
                  background:
                    "linear-gradient(0deg, #FDF7FA 0%, #FDF7FA 100%), #FFF",
                }
              : {}
          }
        >
          <div className="flex justify-between items-center gap-3 mb-3">
            <AlertStatusTag status={getAlertStatus(alert)} />
            {canDelete && (
              <MenuDropdown
                items={menuItems}
                icon={<MoreOptionsIcon />}
                placement="bottomRight"
              />
            )}
          </div>

          <Text
            className="text-[#2B4752] text-base font-semibold"
            ellipsis={{
              tooltip: getAlertDisplayCondition(alert),
            }}
            style={{ margin: 0, marginBottom: "0.25rem" }}
          >
            {getAlertDisplayCondition(alert)}
          </Text>

          <Paragraph
            className="text-[#00000073] text-sm"
            ellipsis={{
              rows: 2,
              tooltip: alert.insight.title,
            }}
            style={{ margin: 0 }}
          >
            {alert.insight.title}
          </Paragraph>
        </div>

        <div
          className="border-t"
          style={{ borderColor: "rgba(0, 0, 0, 0.12)" }}
        />

        <div
          className="px-4 py-3 flex items-center justify-between"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}
        >
          <div className="flex items-center gap-3">
            <Text className="text-[#00000073] text-sm">
              Updated {formattedDate(alert.updated_at, "en-US")}
            </Text>
            <div className="flex items-center gap-1">
              <CadenceIcon className="w-4 h-4" />
              <Text className="text-[#00000073] text-sm">
                {capitalizeFirst(alert.insight.cadence)}
              </Text>
            </div>
          </div>

          {alert.insight.is_shared && (
            <div className="flex items-center gap-1 text-[#00000073]">
              <SharedIcon className="w-4 h-4" />
              <Text className="text-[#00000073] text-sm" style={{ margin: 0 }}>
                Shared
              </Text>
            </div>
          )}
        </div>
      </div>
      <InsightInformationDrawer
        type="alert"
        insight={alert.insight}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
      <Modal
        title="Delete Alert?"
        open={openDeleteModal}
        centered
        okText="Delete"
        cancelText="Cancel"
        onCancel={() => {
          setOpenDeleteModal(false);
        }}
        footer={
          <div className="flex items-center justify-between mt-7">
            <Button
              type="default"
              className="text-[#2B4752] border border-[#2B4752] rounded-sm"
              onClick={() => setOpenDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="bg-[#980000] active:bg-[#980000]! hover:bg-[#980000]! border-0 rounded-sm"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-[#2B4752] text-base">
          Are you sure you want to delete this alert? You won&apos;t be able to
          recover it.
        </p>
      </Modal>
    </>
  );
}
